import { getVehicleRecommendations } from '../api/vehicle-recommendations-api';
import { Vehicle } from '../api/types';
import { IVehicleTile } from '../types/vehicleTile';
import { statusCodesForUnavailableVehicles } from '../pages/home/utilities/constants';
import { translateVehicleStatus } from '../pages/home/utilities/helpers';
import { IViewedVehicleLocalStorage } from '../pages/home/utilities/types';

export interface IRecommendedVehicleTiles {
    vehicleTiles: IVehicleTile[];
    cohortId?: string;
    testId?: string;
}

interface getRecommendedVehicleTilesParams {
    sourceStockNumbers?: string[];
    visitorId: string;
    userStoreId: string;
}

export const getRecommendedVehicleTiles = ({
    sourceStockNumbers,
    visitorId,
    userStoreId,
}: getRecommendedVehicleTilesParams): Promise<IRecommendedVehicleTiles> => {
    if (visitorId) {
        const sortedSourceStockNumbers = sourceStockNumbers?.sort();
        return getVehicleRecommendations({
            stockNumberList: sortedSourceStockNumbers,
            visitorId,
            userStoreId,
        })
            .then((response) => {
                if (response.items.length > 0) {
                    const vehicleTiles = buildVehicleTilesListFromVehicleList(response.items);
                    const tiles: IRecommendedVehicleTiles = {
                        vehicleTiles: vehicleTiles,
                        cohortId: response.cohortId,
                        testId: response.testId,
                    };
                    return tiles;
                } else {
                    return Promise.resolve({ vehicleTiles: [] } as IRecommendedVehicleTiles);
                }
            })
            .catch(() => {
                return { vehicleTiles: [] } as IRecommendedVehicleTiles;
            });
    } else {
        return Promise.resolve({ vehicleTiles: [] } as IRecommendedVehicleTiles);
    }
};

export const buildOrderedVehicleTileList = (orderedStockNumberList: number[], vehiclesList: Vehicle[]) => {
    const orderedVehicleList: Vehicle[] = [];

    for (let i = 0; i < vehiclesList.length; i++) {
        const orderedIndex = orderedStockNumberList.indexOf(vehiclesList[i].stockNumber);
        orderedVehicleList[orderedIndex] = vehiclesList[i];
    }

    return orderedVehicleList;
};

export const buildVehicleTilesListFromVehicleList = (vehicleList: Vehicle[]): IVehicleTile[] =>
    vehicleList.map((vehicle: Vehicle): IVehicleTile => {
        const heroImagePath = `https://img2.carmax.com/assets/${vehicle.stockNumber}/hero.jpg?`;
        const imageLinkControl = `${heroImagePath}width=270&height=203`;
        const imageLinkVariant = `${heroImagePath}width=400`;
        const imageLink = window.featureFlags.enableUFTHome ? imageLinkVariant : imageLinkControl;
        vehicle.imageLink = imageLink;
        return vehicle;
    });

export const isResponseMissingVehicles = (
    vehiclesReturned: Vehicle[],
    vehiclesRequested: number[],
): { isMissing: boolean; missingItems: number[] } => {
    if (vehiclesReturned.length !== vehiclesRequested.length) {
        const foundItems = vehiclesReturned.map((item) => item.stockNumber);

        const notFound = vehiclesRequested?.filter((stockNumber) => {
            const isIncluded = foundItems.includes(stockNumber);
            if (!isIncluded) {
                return stockNumber;
            }
            return;
        });

        return {
            isMissing: notFound.length > 0,
            missingItems: notFound,
        };
    }
    return {
        isMissing: false,
        missingItems: [],
    };
};

export const buildStatusText = (vehicleTile: IVehicleTile): string => {
    return translateVehicleStatus(vehicleTile);
};

export const vehicleIsUnavailable = (statusCode: number, isSaleable: boolean): boolean => {
    return !isSaleable && statusCodesForUnavailableVehicles.indexOf(statusCode) > -1;
};

export const getSortedViewedVehicleStockNumbersByDate = (vehicles: IViewedVehicleLocalStorage[]): number[] => {
    return vehicles
        .sort(({ timestamp: a }: IViewedVehicleLocalStorage, { timestamp: b }: IViewedVehicleLocalStorage) => b - a)
        .map(({ stockNumber }: IViewedVehicleLocalStorage) => Number(stockNumber));
};
